<template>
  <div class="min-h-screen bg-secondary-50 dark:bg-secondary-900">
    <div class="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <Link
          href="/"
          class="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-4"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2" />
          Back to Home
        </Link>
        <h1 class="text-4xl font-bold text-secondary-900 dark:text-white mb-2">
          Terms of Service
        </h1>
        <p class="text-secondary-600 dark:text-secondary-400">
          Last Updated: 4 November 2024
        </p>
      </div>

      <!-- Content -->
      <div class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-8 space-y-8">
        <!-- 1. Introduction -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">1. Introduction and Acceptance of Terms</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              Welcome to Margin Resolve. These Terms of Service ("Terms", "Agreement") govern your access to and use of Margin Resolve,
              a software-as-a-service platform (the "Service") provided by Margin Solutions Ltd ("we", "us", "our", "Company").
            </p>
            <p class="mt-4">
              <strong>Company Information:</strong><br>
              Margin Resolve is a product of Margin Solutions Ltd<br>
              Company Registration Number: SC744893<br>
              Registered in: United Kingdom<br>
              Registered Office: Suite 3.10, 16 Robertson Street, Glasgow, Scotland, G2 8DS<br>
              Contact Email: <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a><br>
              General Enquiries: <a href="mailto:info@marginsolutions.co.uk" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginsolutions.co.uk</a>
            </p>
            <p class="mt-4">
              By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these Terms,
              you do not have permission to access the Service. Your continued use of the Service constitutes acceptance of any
              modifications to these Terms.
            </p>
          </div>
        </section>

        <!-- 2. Definitions -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">2. Definitions</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>For the purposes of these Terms:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>"Account"</strong> means the unique account created for you to access our Service.</li>
              <li><strong>"Content"</strong> means any data, text, graphics, photos, information, or other materials uploaded, collected, generated, stored, or provided by you in connection with the Service.</li>
              <li><strong>"Customer Data"</strong> means all electronic data, text, messages, or other materials submitted by you or on your behalf to the Service.</li>
              <li><strong>"Subscription"</strong> means the paid access to the Service for a specified period.</li>
              <li><strong>"User"</strong> or "you" means the individual accessing or using the Service, or the company or legal entity on behalf of which such individual is accessing or using the Service.</li>
            </ul>
          </div>
        </section>

        <!-- 3. Service Description -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">3. Service Description</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              Margin Resolve is a cloud-based software platform providing incident management, ticketing, maintenance tracking,
              and related business management tools. The Service is provided on a subscription basis and is accessible via web browsers.
            </p>
            <p class="mt-4">
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice.
              We will not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.
            </p>
          </div>
        </section>

        <!-- 4. Account Registration and Eligibility -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">4. Account Registration and Eligibility</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>4.1 Eligibility:</strong> You must be at least 18 years old and capable of forming a binding contract to use this Service. By using the Service, you represent and warrant that you meet these requirements.</p>
            <p class="mt-4"><strong>4.2 Account Creation:</strong> To access certain features of the Service, you must register for an account. You agree to:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Immediately notify us of any unauthorized use of your account</li>
            </ul>
            <p class="mt-4"><strong>4.3 Account Security:</strong> You are solely responsible for maintaining the confidentiality of your login credentials. We are not liable for any loss or damage arising from your failure to protect your account information.</p>
          </div>
        </section>

        <!-- 5. Subscription and Payment Terms -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">5. Subscription and Payment Terms</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>5.1 Subscription Plans:</strong> The Service is offered on various subscription plans with different features and pricing. Details of each plan are available on our website.</p>
            <p class="mt-4"><strong>5.2 Payment:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Subscription fees are billed in advance on a recurring basis (monthly or annually)</li>
              <li>Payment is due immediately upon subscription or renewal</li>
              <li>All fees are non-refundable except as expressly provided in these Terms</li>
              <li>We use third-party payment processors and are not responsible for their errors or omissions</li>
            </ul>
            <p class="mt-4"><strong>5.3 Auto-Renewal:</strong> Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date. By subscribing, you authorize us to charge your payment method for renewal fees.</p>
            <p class="mt-4"><strong>5.4 Price Changes:</strong> We reserve the right to modify subscription fees at any time. Price changes will take effect at the start of the next billing cycle following notice to you. Continued use of the Service after a price change constitutes acceptance of the new price.</p>
            <p class="mt-4"><strong>5.5 Taxes:</strong> All fees are exclusive of applicable taxes, levies, or duties. You are responsible for payment of all such taxes, except for taxes based on our net income.</p>
            <p class="mt-4"><strong>5.6 Failed Payments:</strong> If payment fails, we may suspend or terminate your access to the Service. You remain responsible for any outstanding amounts.</p>
          </div>
        </section>

        <!-- 6. Free Trials and Promotions -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">6. Free Trials and Promotional Offers</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              We may offer free trials or promotional pricing at our discretion. These offers are subject to additional terms which
              will be communicated at the time of the offer. We reserve the right to determine eligibility for free trials and
              promotional offers and to modify or terminate such offers at any time without notice.
            </p>
          </div>
        </section>

        <!-- 7. Acceptable Use Policy -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">7. Acceptable Use Policy</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>7.1 Permitted Use:</strong> You may use the Service only for lawful purposes and in accordance with these Terms.</p>
            <p class="mt-4"><strong>7.2 Prohibited Activities:</strong> You agree not to:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Use the Service in any way that violates applicable laws or regulations</li>
              <li>Impersonate or attempt to impersonate the Company, employees, other users, or any other person or entity</li>
              <li>Upload or transmit viruses, malware, or any other malicious code</li>
              <li>Attempt to gain unauthorized access to the Service, servers, or networks</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              <li>Use any robot, spider, scraper, or other automated means to access the Service</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Remove, alter, or obscure any proprietary notices on the Service</li>
              <li>Use the Service to transmit spam, chain letters, or other unsolicited communications</li>
              <li>Use the Service to store or transmit content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable</li>
              <li>Use the Service to infringe upon intellectual property rights of others</li>
              <li>Resell, lease, or provide access to the Service to third parties without our prior written consent</li>
            </ul>
            <p class="mt-4"><strong>7.3 Enforcement:</strong> We reserve the right, but are not obligated, to monitor use of the Service and to investigate violations of these Terms. We may suspend or terminate your access for violations.</p>
          </div>
        </section>

        <!-- 8. User Content and Data -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">8. User Content and Data</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>8.1 Your Ownership:</strong> You retain all ownership rights in your Customer Data. We do not claim any ownership over your Customer Data.</p>
            <p class="mt-4"><strong>8.2 License Grant:</strong> You grant us a limited, non-exclusive, royalty-free license to access, use, process, copy, distribute, perform, export, and display your Customer Data only as necessary to provide the Service and as permitted by these Terms.</p>
            <p class="mt-4"><strong>8.3 Your Responsibilities:</strong> You are solely responsible for:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>The accuracy, quality, and legality of your Customer Data</li>
              <li>The means by which you acquired Customer Data</li>
              <li>Ensuring you have all necessary rights to submit Customer Data to the Service</li>
              <li>Compliance with all applicable data protection and privacy laws</li>
            </ul>
            <p class="mt-4"><strong>8.4 Data Security:</strong> We implement commercially reasonable security measures to protect Customer Data. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
            <p class="mt-4"><strong>8.5 Data Backup:</strong> While we perform regular backups, you are responsible for maintaining your own backup copies of Customer Data. We are not liable for any loss or corruption of Customer Data.</p>
            <p class="mt-4"><strong>8.6 Data Retention:</strong> Upon termination of your account, we will delete your Customer Data within a reasonable period, subject to our legal obligations and backup retention policies.</p>
          </div>
        </section>

        <!-- 9. Intellectual Property Rights -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">9. Intellectual Property Rights</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>9.1 Our Ownership:</strong> The Service, including all software, technology, designs, graphics, user interfaces, trademarks, logos, and documentation (collectively "Company IP"), is owned by Margin Solutions Ltd and is protected by copyright, trademark, and other intellectual property laws.</p>
            <p class="mt-4"><strong>9.2 Limited License:</strong> Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes during the subscription term.</p>
            <p class="mt-4"><strong>9.3 Restrictions:</strong> You may not:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Copy, modify, or create derivative works of the Service</li>
              <li>Use Company IP for any purpose not expressly permitted by these Terms</li>
              <li>Register or use any trademarks, trade names, or service marks confusingly similar to ours</li>
            </ul>
            <p class="mt-4"><strong>9.4 Feedback:</strong> If you provide feedback, suggestions, or ideas about the Service, we may use such feedback without any obligation to you.</p>
          </div>
        </section>

        <!-- 10. Privacy and Data Protection -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">10. Privacy and Data Protection</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              Our collection and use of personal information is governed by our Privacy Policy. By using the Service, you consent
              to our collection and use of personal data as described in the Privacy Policy. You acknowledge that we may process
              personal data in accordance with applicable data protection laws, including the UK General Data Protection Regulation (UK GDPR).
            </p>
            <p class="mt-4">
              <strong>Data Processing:</strong> To the extent you provide us with personal data of your customers, employees, or other third parties,
              you represent and warrant that you have obtained all necessary consents and have the legal right to share such data with us for
              processing in accordance with these Terms.
            </p>
          </div>
        </section>

        <!-- 11. Service Availability and Support -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">11. Service Availability and Support</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>11.1 Availability:</strong> We strive to provide reliable service availability but do not guarantee that the Service will be uninterrupted, timely, secure, or error-free. Scheduled maintenance and updates may result in temporary unavailability.</p>
            <p class="mt-4"><strong>11.2 No SLA:</strong> Unless otherwise specified in a separate written agreement, we do not provide service level agreements (SLAs) or uptime guarantees.</p>
            <p class="mt-4"><strong>11.3 Support:</strong> Support is provided via email at <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a>. Support availability and response times may vary based on your subscription plan.</p>
            <p class="mt-4"><strong>11.4 Updates:</strong> We may update, enhance, or modify the Service from time to time, including by adding or removing features. We will use reasonable efforts to notify you of material changes.</p>
          </div>
        </section>

        <!-- 12. Third-Party Services and Links -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">12. Third-Party Services and Links</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              The Service may integrate with or contain links to third-party websites, services, or applications. We do not control,
              endorse, or assume responsibility for any third-party services. Your use of third-party services is at your own risk
              and subject to the third party's terms and conditions. We are not liable for any harm or damages related to third-party services.
            </p>
          </div>
        </section>

        <!-- 13. Disclaimers and Warranties -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">13. Disclaimers and Warranties</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>13.1 AS-IS BASIS:</strong> THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</p>
            <p class="mt-4"><strong>13.2 NO WARRANTIES:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</li>
              <li>WARRANTIES THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE</li>
              <li>WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR COMPLETENESS OF ANY CONTENT</li>
              <li>WARRANTIES THAT DEFECTS WILL BE CORRECTED</li>
            </ul>
            <p class="mt-4"><strong>13.3 NO PROFESSIONAL ADVICE:</strong> The Service is not intended to provide legal, financial, medical, or other professional advice. You should consult appropriate professionals for such advice.</p>
            <p class="mt-4"><strong>13.4 RISK ASSUMPTION:</strong> You acknowledge that your use of the Service is at your sole risk. You are solely responsible for any damage to your systems or loss of data resulting from use of the Service.</p>
          </div>
        </section>

        <!-- 14. Limitation of Liability -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">14. Limitation of Liability</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>14.1 EXCLUSION OF DAMAGES:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL MARGIN SOLUTIONS LTD, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS BE LIABLE FOR:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>ANY LOSS OF PROFITS, REVENUE, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES</li>
              <li>DAMAGES RESULTING FROM UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA</li>
              <li>DAMAGES RESULTING FROM ANY SERVICE INTERRUPTION OR UNAVAILABILITY</li>
              <li>DAMAGES RESULTING FROM THIRD-PARTY CONDUCT OR CONTENT ON THE SERVICE</li>
              <li>DAMAGES RESULTING FROM ANY DATA LOSS OR CORRUPTION</li>
            </ul>
            <p class="mt-4">EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</p>
            <p class="mt-4"><strong>14.2 LIABILITY CAP:</strong> OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR ONE HUNDRED POUNDS (£100), WHICHEVER IS GREATER.</p>
            <p class="mt-4"><strong>14.3 EXCEPTIONS:</strong> Some jurisdictions do not allow the exclusion or limitation of certain warranties or damages. In such jurisdictions, our liability will be limited to the maximum extent permitted by law.</p>
            <p class="mt-4"><strong>14.4 BASIS OF THE BARGAIN:</strong> You acknowledge that the fees charged reflect the allocation of risk set forth in these Terms and that we would not provide the Service without these limitations.</p>
          </div>
        </section>

        <!-- 15. Indemnification -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">15. Indemnification</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              You agree to indemnify, defend, and hold harmless Margin Solutions Ltd and its officers, directors, employees, agents,
              affiliates, and licensors from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees
              (including reasonable attorneys' fees) arising from:
            </p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party, including intellectual property rights</li>
              <li>Your Customer Data or any content you submit</li>
              <li>Your violation of any applicable laws or regulations</li>
            </ul>
            <p class="mt-4">
              We reserve the right to assume exclusive defense and control of any matter subject to indemnification by you,
              in which case you will cooperate with us in asserting any available defenses.
            </p>
          </div>
        </section>

        <!-- 16. Term and Termination -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">16. Term and Termination</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>16.1 Term:</strong> These Terms commence when you first access the Service and continue until terminated in accordance with this section.</p>
            <p class="mt-4"><strong>16.2 Termination by You:</strong> You may cancel your subscription at any time through your account settings. Cancellation will be effective at the end of your current billing period. No refunds will be provided for the remainder of the billing period.</p>
            <p class="mt-4"><strong>16.3 Termination by Us:</strong> We may suspend or terminate your access to the Service immediately, without prior notice or liability, for any reason, including if you:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Breach these Terms</li>
              <li>Fail to pay applicable fees</li>
              <li>Engage in fraudulent or illegal activities</li>
              <li>Violate the Acceptable Use Policy</li>
            </ul>
            <p class="mt-4"><strong>16.4 Effect of Termination:</strong> Upon termination:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Your right to access and use the Service will immediately cease</li>
              <li>You will no longer have access to your Customer Data stored on the Service</li>
              <li>We may delete your Customer Data within a reasonable period</li>
              <li>All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnification, and limitations of liability</li>
            </ul>
            <p class="mt-4"><strong>16.5 Data Export:</strong> You are responsible for exporting your Customer Data before termination. We are not obligated to retain or provide access to Customer Data after termination.</p>
          </div>
        </section>

        <!-- 17. Modifications to Terms -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">17. Modifications to Terms</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the
              updated Terms on our website and updating the "Last Updated" date. Your continued use of the Service after such
              modifications constitutes acceptance of the updated Terms. If you do not agree to the modified Terms, you must
              stop using the Service and cancel your subscription.
            </p>
            <p class="mt-4">
              It is your responsibility to review these Terms periodically. We may also provide notice of changes via email
              to the address associated with your account.
            </p>
          </div>
        </section>

        <!-- 18. Dispute Resolution and Governing Law -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">18. Dispute Resolution and Governing Law</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>18.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of Scotland and the United Kingdom, without regard to conflict of law principles.</p>
            <p class="mt-4"><strong>18.2 Jurisdiction:</strong> You agree to submit to the exclusive jurisdiction of the courts located in Scotland for the resolution of any disputes arising out of or relating to these Terms or the Service.</p>
            <p class="mt-4"><strong>18.3 Dispute Resolution Process:</strong> Before filing any legal action, you agree to first attempt to resolve any dispute informally by contacting us at <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a>. We will attempt to resolve the dispute informally within 30 days.</p>
            <p class="mt-4"><strong>18.4 Class Action Waiver:</strong> To the extent permitted by law, all claims must be brought in the parties' individual capacity, and not as a plaintiff or class member in any class or representative action. You waive any right to participate in a class action or class-wide arbitration.</p>
          </div>
        </section>

        <!-- 19. Export Compliance -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">19. Export Compliance</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              The Service may be subject to export control laws and regulations. You agree to comply with all applicable export
              and re-export control laws and regulations, including the Export Administration Regulations maintained by the
              UK government. You represent that you are not located in, or a national or resident of, any country subject to
              UK government embargo or designated as a "terrorist supporting" country, and that you are not on any list of
              prohibited or restricted parties.
            </p>
          </div>
        </section>

        <!-- 20. Force Majeure -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">20. Force Majeure</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control,
              including acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods,
              accidents, pandemics, strikes, or shortages of transportation, facilities, fuel, energy, labor, or materials.
            </p>
          </div>
        </section>

        <!-- 21. Assignment -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">21. Assignment</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              You may not assign or transfer these Terms or your rights hereunder, in whole or in part, without our prior written consent.
              We may assign these Terms or any rights hereunder without restriction. Any attempted assignment in violation of this section
              shall be void. These Terms shall be binding upon and inure to the benefit of the parties and their permitted successors and assigns.
            </p>
          </div>
        </section>

        <!-- 22. Entire Agreement -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">22. Entire Agreement</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference,
              constitute the entire agreement between you and Margin Solutions Ltd regarding the Service and supersede all
              prior agreements, understandings, and communications, whether written or oral, regarding the subject matter herein.
            </p>
          </div>
        </section>

        <!-- 23. Severability -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">23. Severability</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              If any provision of these Terms is found to be unenforceable or invalid under any applicable law, such unenforceability
              or invalidity shall not render these Terms unenforceable or invalid as a whole. Such provisions shall be deleted without
              affecting the remaining provisions herein, and the invalid or unenforceable provision shall be replaced with a valid and
              enforceable provision that most closely matches the intent of the original provision.
            </p>
          </div>
        </section>

        <!-- 24. Waiver -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">24. Waiver</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term.
              Our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
              Any waiver must be in writing and signed by an authorized representative of Margin Solutions Ltd.
            </p>
          </div>
        </section>

        <!-- 25. Notices -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">25. Notices</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              All notices required or permitted under these Terms shall be in writing and delivered by email, registered mail,
              or courier to the addresses set forth below:
            </p>
            <p class="mt-4">
              <strong>To Margin Solutions Ltd:</strong><br>
              Email: <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a><br>
              Address: Suite 3.10, 16 Robertson Street, Glasgow, Scotland, G2 8DS
            </p>
            <p class="mt-4">
              <strong>To You:</strong> At the email address or postal address associated with your account.
            </p>
            <p class="mt-4">
              Notices shall be deemed given when received if sent by email, or three business days after mailing if sent by registered mail or courier.
            </p>
          </div>
        </section>

        <!-- 26. Complaints Procedure -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">26. Complaints Procedure</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              If you have a complaint about the Service, please contact us at <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a>
              or <a href="mailto:info@marginsolutions.co.uk" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginsolutions.co.uk</a>. We will acknowledge
              receipt of your complaint within 5 business days and will investigate and respond to your complaint within 30 days.
              If you are not satisfied with our response, you may have the right to refer your complaint to relevant regulatory
              authorities or consumer protection agencies.
            </p>
          </div>
        </section>

        <!-- 27. Additional Terms for Specific Features -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">27. Additional Terms for Specific Features</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              Certain features of the Service may be subject to additional terms and conditions, which will be presented to you
              at the time you access such features. Those additional terms are incorporated into these Terms by reference and
              govern your use of those specific features in the event of any conflict with these Terms.
            </p>
          </div>
        </section>

        <!-- 28. User-Generated Content -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">28. User-Generated Content</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              Any content, feedback, suggestions, comments, or other materials you submit through the Service (other than Customer Data)
              may be used by us for any purpose without compensation or attribution to you. You grant us a perpetual, irrevocable,
              worldwide, royalty-free license to use, reproduce, modify, adapt, publish, translate, distribute, and display such
              content in any media.
            </p>
          </div>
        </section>

        <!-- 29. Beta Features -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">29. Beta Features</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              We may offer certain features on a beta or trial basis ("Beta Features"). Beta Features are provided "as is" without
              warranty of any kind and may not function as intended. We may discontinue Beta Features at any time without notice.
              Your use of Beta Features is at your sole risk.
            </p>
          </div>
        </section>

        <!-- 30. Contact Information -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">30. Contact Information</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>If you have any questions about these Terms, please contact us:</p>
            <p class="mt-4">
              <strong>Margin Solutions Ltd</strong><br>
              Suite 3.10, 16 Robertson Street<br>
              Glasgow, Scotland<br>
              G2 8DS<br>
              United Kingdom
            </p>
            <p class="mt-4">
              Email (Margin Resolve): <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a><br>
              Email (General): <a href="mailto:info@marginsolutions.co.uk" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginsolutions.co.uk</a><br>
              Company Registration: SC744893
            </p>
          </div>
        </section>

        <!-- Acknowledgment -->
        <section class="border-t border-secondary-200 dark:border-secondary-700 pt-8">
          <div class="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-2">
              Acknowledgment
            </h3>
            <p class="text-primary-800 dark:text-primary-200">
              BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM,
              AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE THE SERVICE.
            </p>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <div class="mt-8 text-center text-sm text-secondary-500 dark:text-secondary-400">
        <p>© {{ currentYear }} Margin Solutions Ltd. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';

// No layout for terms page (full-page design)
defineOptions({
  layout: null,
});

const currentYear = computed(() => new Date().getFullYear());
</script>

<style scoped>
/* Custom styles without @apply to avoid Tailwind v4 issues */
</style>
